import React from 'react'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {useContentfulData} from '@github-ui/swp-core/lib/hooks/use-contentful-data'
import {toPayload} from '../../../../lib/types/payload'
import {toBrandPage} from '../../../../brand/lib/types/contentful'
import {ThemeProvider} from '@primer/react-brand/esm'

// Custom components
import {DeepDive} from './_components/DeepDive/DeepDive'
import {Gallery} from './_components/Gallery/Gallery'
import {Hero} from './_components/Hero/Hero'
import {Highlight} from './_components/Highlight/Highlight'
import {Media} from './_components/Media/Media'
import {Overview} from './_components/Overview/Overview'
import {River} from './_components/River/River'
import {RiverAccordion} from './_components/RiverAccordion/RiverAccordion'
import {Visual} from './_components/Visual/Visual'

import {Nav} from './_shared/Nav/Nav'
import {NavBack} from './_shared/NavBack/NavBack'

import Styles from './Index.module.css'

export default function EventsUniverseRecapIndex() {
  const {contentfulRawJsonResponse} = toPayload(useRoutePayload<unknown>())
  const contentfulResponse = useContentfulData(contentfulRawJsonResponse)
  const page = toBrandPage(contentfulResponse)
  const nav = page.fields.template.fields.subnav
  const sections = page.fields.template.fields.content || []

  // Create theme map to be used in Nav
  const themeMap: Record<string, string> = {}
  for (const section of sections) {
    const sectionId = section.fields.htmlId
    const theme = section.fields.content?.at(0)?.fields.label?.fields.color || 'green'
    if (sectionId) {
      themeMap[sectionId] = theme
    }
  }

  return (
    <ThemeProvider className={Styles.Page} colorMode="light">
      {sections.map((section, sectionIndex) => {
        //
        // Section
        //
        const sectionId = section.fields.htmlId || '' // Used for deeplinking
        const location = sectionId ? `section_${sectionId}` : `section_${sectionIndex + 1}` // Used for analytics
        const theme = section.fields.content?.at(0)?.fields.label?.fields.color || 'green' // Used for setting a theme color
        const components = section.fields.content || []

        return (
          <React.Fragment key={section.sys?.id || sectionIndex}>
            {/* Render BACK TO UNIVERSE before the first section */}
            {sectionIndex === 0 && nav && <NavBack content={nav} />}

            <section
              className={Styles.Section}
              {...(sectionId && {id: sectionId})}
              {...(theme && {'data-theme': theme})}
            >
              <div className={Styles.Container}>
                {components.map((component, contentIndex) => {
                  //
                  // Component
                  //
                  const contentType = component?.sys.contentType.sys.id // Limited to genericContent
                  const customComponent = component?.fields.id || '' // ID is used for choosing custom component

                  // Add space between components where an asset touches the edge
                  const previousComponent = contentIndex > 0 ? components[contentIndex - 1]?.fields.id || '' : ''
                  const shouldAddSpacing =
                    (['River', 'RiverAccordion', 'Media'].includes(previousComponent) &&
                      customComponent !== 'Highlight') ||
                    (previousComponent === 'Highlight' &&
                      ['River', 'RiverAccordion', 'Media'].includes(customComponent))

                  return (
                    contentType === 'genericContent' && (
                      <div
                        key={component?.sys?.id || contentIndex}
                        className={Styles.Component}
                        {...(sectionIndex === 0 && contentIndex === 0 && {'data-hpc': true})} // Mark first component in first section with data-hpc for performance tracking
                        {...(shouldAddSpacing && {'data-spacing': 'top'})}
                      >
                        {/* DeepDive */}
                        {customComponent === 'DeepDive' && <DeepDive content={component} location={location} />}

                        {/* Gallery */}
                        {customComponent === 'Gallery' && <Gallery content={component} sectionIndex={sectionIndex} />}

                        {/* Hero */}
                        {customComponent === 'Hero' && (
                          <Hero
                            content={component}
                            location={location}
                            sectionId={sectionId}
                            sectionIndex={sectionIndex}
                          />
                        )}

                        {/* Highlight */}
                        {customComponent === 'Highlight' && (
                          <Highlight content={component} location={location} sectionIndex={sectionIndex} />
                        )}

                        {/* Media */}
                        {customComponent === 'Media' && (
                          <Media
                            content={component}
                            location={location}
                            sectionIndex={sectionIndex}
                            loading={sectionIndex > 0 ? 'lazy' : 'eager'} // Load eagerly only in first section
                          />
                        )}

                        {/* Overview */}
                        {customComponent === 'Overview' && (
                          <Overview content={component} location={location} index={contentIndex} />
                        )}

                        {/* River */}
                        {customComponent === 'River' && (
                          <River
                            content={component}
                            location={location}
                            index={contentIndex}
                            sectionIndex={sectionIndex}
                          />
                        )}

                        {/* RiverAccordion */}
                        {customComponent === 'RiverAccordion' && (
                          <RiverAccordion
                            content={component}
                            location={location}
                            index={contentIndex}
                            sectionIndex={sectionIndex}
                          />
                        )}

                        {/* Visual */}
                        {customComponent === 'Visual' && <Visual content={component} />}
                      </div>
                    )
                  )
                })}
              </div>
            </section>

            {/* Render nav after first section */}
            {sectionIndex === 0 && nav && <Nav content={nav} themeMap={themeMap} />}
          </React.Fragment>
        )
      })}
    </ThemeProvider>
  )
}
